import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import { useEffect, useRef, useState } from 'react';
import useDADataTransformations, { DealRecieverDetailMap, MasterData } from './useDADataTransformations';
import {
    createDeal,
    getDealData,
    handleDealApplicationDrawer,
    resetDealFilters,
    setConfigTableModal,
    setDealFilters,
    updateDeal,
    updateDealApplicationData,
} from '../../../store/investor/action';
import { getPlatformUserId, getSubdomain } from '../../../utils/utils';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';
import { isEqual } from 'lodash';
import { toast } from 'react-toastify';
import { DEAL_CREATOR_TYPE } from '../../ManageDealDrawer/DealDrawerConstants';
import { DealFilters, defaultDealFilters } from '../../../store/investor/reducer';
import { updateQueryParams } from '../../../utils/searchParamUtils';
import { DESIGNATION, MANDATE_STATES } from '../../../utils/constants/commonConstants';
import { slateToHtml } from '../../TextEditor/utils/SlateSerialisationUtil';
import { processImagesBeforeSave } from '../../TextEditor/plugins/imageUploadService';
import { ENTITY_TYPES, DEAL_SOURCE_TYPES } from '../../../store/investor/constants';

const useFetchFormData = (enabled: boolean, disabledFields: string[] = []) => {
    const source = useAppSelector((state) => state.investor.dealApplicationDrawer.source);
    const drawerContent = useAppSelector((state) => state.investor.dealApplicationDrawer);
    const [formData, setFormData] = useState<MasterData | null>(null);
    const dealId = useAppSelector((state) => state.investor.dealApplicationDrawer.dealId);
    const platformData = useAppSelector((state) => state.user.domainMetadata);
    const [isFormValid, setIsFormValid] = useState(false);
    const [loading, setLoading] = useState(false);
    const [showConfirmation, setShowConfirmation] = useState(false);
    const params = useParams();
    const dealFilters = useAppSelector((state) => state.investor.dealFilters);
    const [searchParams, setSearchParams] = useSearchParams();
    const [tempFilters, setTempFilters] = useState<DealFilters>(dealFilters);
    const { createDealData } = useDADataTransformations({
        source: source,
    });
    const dealProducts = useAppSelector((state) => state.investor.dealApplicationDrawer.allProducts);
    const user = useAppSelector((state) => state.user);
    const loggedInUser = user?.userData;
    const dispatch = useAppDispatch();
    const location = useLocation();
    const masterData = useAppSelector((state) => state.investor.dealApplicationDrawer.dealMasterData);
    const selectedStep = useAppSelector((state) => state.investor.dealApplicationDrawer.step);
    const [step, setStep] = useState(selectedStep ?? 0);
    const [orgMaps, setOrgMaps] = useState<Record<string, { label: string; value: string; userType?: string }[]>>({});

    // Use a ref to keep track of the last submitted form data.
    // This allows us to know whether the user's current formData differs from the last submitted version.
    const lastSubmittedDataRef = useRef<MasterData | null>(null);
    const dealTermsChanged = useRef<object | null>(null);
    const platformUserId = getPlatformUserId();
    // For multi-step navigation
    useEffect(() => {
        setTempFilters(dealFilters);
    }, [dealFilters]);

    useEffect(() => {
        if (selectedStep) {
            setStep(selectedStep);
        }
    }, [selectedStep]);

    const getFormData = () => {
        if (source === DEAL_SOURCE_TYPES.DEBT_PRODUCTS) return makeFormDataFromState();
        if (source === DEAL_SOURCE_TYPES.CREATE_DEAL || source === DEAL_SOURCE_TYPES.EDIT_DEAL) return fetchFormDataFromApi();
    };

    const getTransformedData = (data: any, api: string, orgId: string) => {
        if (!data) return [];
        if (api === 'GET_ALL_PRODUCTS') {
            const transformedData = Object.values(data)?.map((item: any) => ({
                label: item.productName,
                value: item.productId,
            }));
            return transformedData;
        } else if (api === 'AICA_GET_ORG_USERS') {
            const mappedData = Object.values(data || {}).map((item: any) => ({
                label: item.name,
                value: item.userId,
                type: item.userType === 'LENDER' ? 'Admin' : 'Team Member',
            }));
            setOrgMaps({ ...orgMaps, [orgId]: mappedData.map(({ label, value, type }) => ({ label, value, type })) });

            return mappedData;
        } else {
            const transformedData = Object.values(data)?.map((item: any) => ({
                label: item.label,
                value: item._id,
            }));
            return transformedData;
        }
    };

    const emptyFormData = { products: [], dealTerms: {}, dealReceiverDetailMap: new Map() };

    const fetchFormDataFromApi = () => {
        dispatch(
            getDealData(
                { dealId },
                {
                    onSuccess: (deal) => {
                        dispatch(
                            handleDealApplicationDrawer({
                                open: true,
                                source,
                                width: '67%',
                                heading: drawerContent.heading,
                                subHeading: drawerContent.subHeading,
                                dealBeneficiaryId: drawerContent.dealBeneficiaryId,
                                dealId: deal.id,
                            }),
                        );
                        setFormData(createDealData(deal) ?? emptyFormData);
                    },
                },
            ),
        );
    };

    const makeFormDataFromState = () => setFormData(masterData ?? emptyFormData);

    useEffect(() => {
        if (searchParams.get('dealCreated') !== 'true') {
            dealTermsChanged.current = formData?.dealTerms ?? null;
        }
    }, [step]);

    const updateDealTerms = (key: string, value: string | string[] | (string | null)[]) => {
        if (formData) {
            // Filter out null values if array
            let cleanedValue: string | string[];
            if (Array.isArray(value)) {
                cleanedValue = value.filter((v): v is string => v !== null) as string[];
            } else {
                cleanedValue = value;
            }

            let updatedFormData: MasterData = {
                ...formData,
                dealTerms: {
                    ...formData.dealTerms,
                    [key]: cleanedValue,
                },
            };
            if (key === 'loanTypes') {
                // Simply update products with the new value
                updatedFormData = {
                    ...updatedFormData,
                    products: Array.isArray(cleanedValue) ? [...cleanedValue] : [],
                };
            }

            setFormData(updatedFormData);
        }
    };

    const updateLenderMap = (lenderDetails: DealRecieverDetailMap) => {
        if (!formData) return;
        if (searchParams.get('dealCreated') === 'true') {
            searchParams.delete('dealCreated');
            setSearchParams(searchParams);
        }
        setFormData({
            ...formData,
            dealReceiverDetailMap: lenderDetails,
        });
    };

    useEffect(() => {
        if (formData) {
            const payload: any = {
                masterData: {
                    dealTerms: formData.dealTerms,
                    products: formData.products,
                    dealReceiverDetailMap: formData.dealReceiverDetailMap,
                },
            };

            if (drawerContent?.heading === 'Add Lender') {
                payload.step = 1;
            }

            dispatch(updateDealApplicationData(payload));
        }
    }, [formData]);

    const checkDiffOfDealTermsVSLenders = () => {
        // Check if formData.dealTerms is different from formData.dealReceiverDetailMap.dealTerms
        if (!formData) return false;
        for (const [key, value] of formData.dealReceiverDetailMap as DealRecieverDetailMap) {
            if (!isEqual(value.dealTerms, formData.dealTerms)) {
                return true;
            }
        }
        return false;
    };

    const getDealStatus = (step: number, isPrimaryClick: boolean, payload: any) => {
        if (source === 'editDeal') {
            if (step === 0) {
                return formData?.dealStatus;
            } else {
                const hasNewLenders =
                    Object.keys(payload.dealReceiverDetailMap).filter((key) => !payload.dealReceiverDetailMap[key].isExisting).length > 0;

                return hasNewLenders && isPrimaryClick ? 'SHARED_DEAL' : 'SHARED';
            }
        } else {
            return step === 1 && isPrimaryClick ? 'SHARED_DEAL' : formData?.dealStatus ?? 'DRAFT';
        }
    };

    const getDealRecieverMap = () => {
        if (!formData?.dealReceiverDetailMap) {
            return {};
        }
        // Convert the map into an array, process each entry, then build an object back
        const entries = Array.from(formData.dealReceiverDetailMap).reduce((acc: [string, any][], [key, value]: [string, any]): any => {
            // Skip existing entries if source is 'editTerms'
            if (source === 'editDeal' && value.isExisting) {
                return acc;
            }

            // Map loanType values to their labels
            const mappedLoanTypes = value.dealTerms?.loanTypes?.map((loanType: string) => {
                const matchedProduct = dealProducts?.find((product) => product.value === loanType);
                return matchedProduct?.label ?? loanType;
            });

            // Decide which dealTerms to use
            let dealTerms: any;
            if (searchParams.get('dealCreated') === 'true') {
                dealTerms = {
                    ...formData.dealTerms,
                    loanTypes: mappedLoanTypes,
                    dealNotes: undefined, // remove dealNotes
                };
            } else {
                dealTerms = {
                    ...value.dealTerms,
                    loanTypes: mappedLoanTypes,
                    dealNotes: undefined, // remove dealNotes
                };
            }
            if (dealTerms.hasOwnProperty('lenderUsers')) {
                const orgMapForKey = orgMaps[key];
                const mappedUsers =
                    orgMapForKey
                        ?.filter((user) => dealTerms.lenderUsers.includes(user.value))
                        .filter((user) => !user.userType || user.userType !== DESIGNATION.ADMIN) // By default All deals are sent to the Admin. Therefore, we filter out Admin users
                        .map((user) => ({
                            name: user.label,
                            userId: user.value,
                        })) || [];

                dealTerms.lenderUsers = {
                    userIds: mappedUsers,
                    type: mappedUsers.length > 0 ? 'SELECTED' : 'ALL',
                };
            } else {
                dealTerms.lenderUsers = { userIds: [], type: 'ALL' };
            }

            acc.push([
                key,
                {
                    dealTerms,
                    products: value.productEnums,
                },
            ]);

            return acc;
        }, []);

        // Build the final object from the processed entries
        return Object.fromEntries(entries);
    };

    const createDealPayload = async (
        formData: MasterData,
        step: number,
        isPrimaryClick: boolean,
        avoidDealReceiverDetailMap: boolean = false,
    ) => {
        // Filter out disabled fields and transform loanTypes IDs to labels
        const filteredTerms = Object.fromEntries(Object.entries(formData.dealTerms || {}).filter(([key]) => !disabledFields.includes(key)));
        const dealTerms = {
            ...filteredTerms,
        };

        const payload: any = {
            dealTerms,
            ...(!disabledFields.includes('loanTypes') && { products: formData.products }),
            dealProposerId: platformData?.investorOrgId,
            dealBeneficiaryId: params.id ?? drawerContent.dealBeneficiaryId,
            dealCreatedBy: loggedInUser?.userName,
            dealReceiverDetailMap: getDealRecieverMap(),
        };

        if ((formData.mandateState === MANDATE_STATES.SHARED && !isPrimaryClick) || avoidDealReceiverDetailMap) {
            delete payload.dealReceiverDetailMap;
        }

        if (dealId) payload.dealId = dealId;
        if (!dealId || step === 1) {
            payload.owner = {
                name: loggedInUser?.userName,
                userId: getPlatformUserId(),
                activityBy: loggedInUser?.userName,
                activityDate: new Date().toISOString(),
                ownerType: DEAL_CREATOR_TYPE,
            };
        }
        if (payload.dealTerms.hasOwnProperty('dealNotes')) {
            const processedNotes = await processImagesBeforeSave(payload.dealTerms.dealNotes || [], [], 'deal-notes');
            payload.dealTerms.dealNotes = [
                {
                    note: slateToHtml(processedNotes),
                    noteBy: loggedInUser?.userName,
                    noteDate: new Date().toISOString(),
                },
            ];
        }
        payload.userId = platformUserId;
        return payload;
    };

    const closeDrawer = () => {
        dispatch(
            handleDealApplicationDrawer({
                open: false,
                width: '0',
                source: '',
                dealId: '',
                heading: '',
                subHeading: '',
            }),
        );
    };
    const handleSecondaryClick = () => {
        if (source === 'DEAL_FILTERS') {
            dispatch(resetDealFilters());
            setTempFilters({ ...defaultDealFilters, statuses: [] });
            return;
        }
        if (step === 0) return;
        setStep((prev) => Math.max(prev - 1, 0));
    };

    const handleUpdateDeal = (payload: any, hideShareDeal: boolean = false) => {
        dispatch(
            updateDeal(payload, {
                onSuccess: () => {
                    if (step === 1 || hideShareDeal) {
                        dispatch(
                            handleDealApplicationDrawer({
                                open: false,
                                source: source,
                                dealId: '',
                                width: '',
                                heading: '',
                                subHeading: '',
                                showSuccessModal: !hideShareDeal,
                                profileGenerationStatus: '',
                            }),
                        );
                        dispatch(
                            updateDealApplicationData({
                                masterData: {
                                    products: [],
                                    dealTerms: {},
                                    dealReceiverDetailMap: new Map(),
                                },
                            }),
                        );
                    } else {
                        setStep((prev) => prev + 1);
                    }
                },
            }),
        );
    };

    const handlePrimaryClick = async (overrideCheck = false) => {
        switch (source) {
            case 'DEAL_FILTERS':
                if (tempFilters) {
                    dispatch(setDealFilters(tempFilters));
                }
                closeDrawer();
                break;
            default:
                setLoading(true);
                if (formData) {
                    if (
                        checkDiffOfDealTermsVSLenders() &&
                        !overrideCheck &&
                        step === 0 &&
                        dealTermsChanged.current !== null &&
                        searchParams.get('dealCreated') !== 'true' &&
                        !isEqual(formData.dealTerms, dealTermsChanged.current)
                    ) {
                        setShowConfirmation(true);
                        setLoading(false);
                        return;
                    }
                    const payload = await createDealPayload(
                        formData,
                        step,
                        true,
                        step === 0 && formData.mandateState === MANDATE_STATES.SHARED,
                    );
                    if (source === 'debtProducts' || source === 'createDeal' || source === 'editDeal') {
                        // If this is the very first submission OR if the formData is different from the last submitted version,
                        // then we want to dispatch the API call.
                        const hideShareDeal =
                            step === 0 && (location.pathname.includes('deals') || location.pathname.includes('company-deals'));
                        if (
                            step === 1 ||
                            hideShareDeal ||
                            !lastSubmittedDataRef.current ||
                            !isEqual(formData, lastSubmittedDataRef.current)
                        ) {
                            if (source === 'editDeal' || source === 'createDeal') {
                                updateQueryParams(searchParams, setSearchParams, { editDealUpdated: true }, location);
                            }
                            if (dealId) {
                                // Only show config modal if step === 1, otherwise call handleUpdateDeal directly
                                if (step === 1) {
                                    dispatch(
                                        setConfigTableModal({
                                            show: true,
                                            data: {
                                                entity: ENTITY_TYPES.MANDATE,
                                                actionType: 'shareDeal',
                                                rowData: formData,
                                                source: [ENTITY_TYPES.LEAD, ENTITY_TYPES.MANDATE], // Save to both Lead and Deal/Mandate when modal is shown
                                                dealId: dealId,
                                                orgId: drawerContent.dealBeneficiaryId, // Pass orgId for Lead data fetching
                                                mandateId: dealId,
                                                onSuccess: () => {
                                                    handleUpdateDeal(payload);
                                                },
                                            },
                                        }),
                                    );
                                } else {
                                    // Call handleUpdateDeal directly without showing modal
                                    handleUpdateDeal(payload, hideShareDeal);
                                }

                                // Update the reference to the last submitted data.
                                lastSubmittedDataRef.current = formData;
                            } else {
                                dispatch(
                                    createDeal(payload, {
                                        onSuccess: (dealId) => {
                                            dispatch(
                                                handleDealApplicationDrawer({
                                                    open: true,
                                                    source: source,
                                                    heading: drawerContent.heading,
                                                    dealBeneficiaryId: drawerContent.dealBeneficiaryId,
                                                    subHeading: drawerContent.subHeading,
                                                    dealId: dealId,
                                                    width: '67%',
                                                }),
                                            );
                                            setStep((prev) => prev + 1);
                                            lastSubmittedDataRef.current = formData;
                                        },
                                    }),
                                );
                            }
                        } else {
                            setStep((prev) => prev + 1);
                        }
                    }
                }
                setLoading(false);
                break;
        }
    };

    useEffect(() => {
        if (!enabled) {
            setFormData({
                products: [],
                dealTerms: {},
                dealReceiverDetailMap: new Map(),
            });
            setStep(0);
            return;
        }
        if (source) {
            getFormData();
        }
    }, [source]);

    return {
        formData,
        step,
        setStep,
        updateDealTerms,
        handleBack: handleSecondaryClick,
        handlePrimaryClick,
        updateLenderMap,
        loading,
        setIsFormValid,
        isFormValid,
        showConfirmation,
        setShowConfirmation,
        tempFilters,
        setTempFilters,
        dealProducts,
        getTransformedData,
        searchParams,
        setSearchParams,
    };
};

export default useFetchFormData;
